<template>
  <div 
    class="file-upload"
    :class="{ 'dragging': isDragging }"
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
    @dragover.prevent
    @drop.prevent="handleDrop"
    @click="triggerFileInput"
  >
    <input
      ref="fileInput"
      type="file"
      @change="handleFileSelect"
      :accept="accept"
      style="display: none"
    >
    
    <div v-if="!isUploading && !error" class="upload-placeholder">
      <div class="upload-icon">
        <FontAwesomeIcon icon="upload" size="2x" />
      </div>
      <div class="upload-text">
        Drop a file here or click to upload
      </div>
      <div v-if="reqFileWidth && reqFileHeight" class="upload-requirements">
        Required resolution: {{ reqFileWidth }}x{{ reqFileHeight }}
      </div>
      <div class="upload-requirements">
        Maximum file size: {{ formatFileSize(maxFileSize) }}
      </div>
    </div>

    <div v-else-if="error" class="upload-error">
      <div class="error-icon">
        <FontAwesomeIcon icon="exclamation-circle" size="2x" />
      </div>
      <div class="error-message">{{ error }}</div>
      <button class="retry-button" @click="clearError">Try Again</button>
    </div>

    <div v-else class="upload-progress">
      <div class="progress-text">
        Uploading {{ currentFile.name }} ({{ formatFileSize(currentFile.size) }})...
      </div>
      <div class="progress-bar-container">
        <div 
          class="progress-bar" 
          :style="{ width: `${uploadProgress}%` }"
        ></div>
      </div>
      <div class="progress-percentage">
        {{ Math.round(uploadProgress) }}%
      </div>
      <div class="upload-details">
        <div class="upload-speed">{{ formatFileSize(uploadSpeed) }}/s</div>
        <div class="remaining-time">{{ formatEstimatedDuration(remainingTime) || 'Unknown time' }} remaining</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { FontAwesomeIcon } from '@/scripts/icons';
import { formatFileSize, formatEstimatedDuration } from '@/scripts/shared-utils';
import { isVideoFile, extractVideoMetadata, initMediaInfo } from '@/scripts/shared-mediainfo';
import { getUploadUrl, uploadFile, completeUpload } from '@/scripts/api/assetApi';

const props = defineProps({
  maxFileSize: {
    type: Number,
    required: true
  },
  reqFileWidth: {
    type: Number,
    default: 0
  },
  reqFileHeight: {
    type: Number,
    default: 0
  },
  accept: {
    type: String,
    default: '*/*'
  },
  folder: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['error', 'success']);

const isDragging = ref(false);
const isUploading = ref(false);
const currentFile = ref(null);
const fileInput = ref(null);
const error = ref('');

const PROGRESS_UPDATE_INTERVAL = 1000; // 1 second
const SMOOTHING_FACTOR = 0.75; // Smoothing factor for upload speed

// Track progress per chunk but speed globally
const chunkProgress = ref([]); // bytes uploaded per chunk
const lastTotalUploaded = ref(0); // last total bytes uploaded
const lastUpdateTime = ref(0); // last update time for speed calculation
const uploadSpeed = ref(0); // bytes per second
const smoothUploadSpeed = ref(0); // smoothed bytes per second

// Computed values for overall upload stats
const totalSize = computed(() => currentFile.value?.size || 0);
const totalUploaded = computed(() => chunkProgress.value.reduce((sum, bytes) => sum + bytes, 0));
const uploadProgress = computed(() => totalSize.value ? (totalUploaded.value * 100 / totalSize.value) : 0);
const remainingTime = computed(() => {
  const remainingBytes = totalSize.value - totalUploaded.value;
  return smoothUploadSpeed.value > 0 ? remainingBytes / smoothUploadSpeed.value : 0;
});

// Update upload speed based on total progress
const updateUploadSpeed = () => {
  const now = Date.now();
  const timeDiff = now - lastUpdateTime.value;
  
  if (timeDiff > PROGRESS_UPDATE_INTERVAL) {
    const bytesDiff = totalUploaded.value - lastTotalUploaded.value;
    uploadSpeed.value = bytesDiff / (timeDiff / 1000);
    smoothUploadSpeed.value = (smoothUploadSpeed.value * SMOOTHING_FACTOR) + (uploadSpeed.value * (1 - SMOOTHING_FACTOR));
    
    lastUpdateTime.value = now;
    lastTotalUploaded.value = totalUploaded.value;
  }
};

const clearError = () => {
  error.value = '';
};

const triggerFileInput = () => {
  fileInput.value.click();
};

const handleDragEnter = () => {
  isDragging.value = true;
};

const handleDragLeave = () => {
  isDragging.value = false;
};

const handleDrop = (e) => {
  isDragging.value = false;
  const file = e.dataTransfer.files[0];
  if (file) {
    processFile(file);
  }
};

const handleFileSelect = (e) => {
  const file = e.target.files[0];
  if (file) {
    processFile(file);
  }
};

const getImageMetadata = async (file) => {
  // Only process image files
  if (!file.type.startsWith('image/')) {
    return {};
  }

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.width,
        height: img.height
      });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({});
    };

    img.src = url;
  });
};

const getVideoMetadata = async (file) => {
  if (!isVideoFile(file)) {
    return {};
  }

  try {
    return await extractVideoMetadata(file);
  } catch (e) {
    console.warn('Error reading video metadata:', e);
    return {};
  }
};

const handleSingleUpload = async (file, uploadData) => {
  // Initialize tracking for single upload
  chunkProgress.value = [0];
  lastUpdateTime.value = Date.now();
  lastTotalUploaded.value = 0;
  uploadSpeed.value = 0;
  
  await uploadFile(
    uploadData.url, 
    file,
    {
      onUploadProgress: (progressEvent) => {
        // Update progress for the single chunk
        chunkProgress.value[0] = progressEvent.loaded;
        
        // Update global upload speed
        updateUploadSpeed();
      }
    }
  );
  return uploadData.location;
};

const handleMultipartUpload = async (file, uploadData) => {
  const { parts, upload_id, file_key } = uploadData;
  const chunkSize = Math.ceil(file.size / parts.length);
  const uploadedParts = [];
  
  // Initialize tracking arrays for all chunks
  chunkProgress.value = Array(parts.length).fill(0);
  lastUpdateTime.value = Date.now();
  lastTotalUploaded.value = 0;
  uploadSpeed.value = 0;

  for (let i = 0; i < parts.length; i++) {
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, file.size);
    const chunk = file.slice(start, end);

    const response = await uploadFile(
      parts[i].url, 
      chunk,
      { 
        headers: { 'Content-Type': 'application/octet-stream' },
        onUploadProgress: (progressEvent) => {
          // Update progress for this chunk
          chunkProgress.value[i] = progressEvent.loaded;
          
          // Update global upload speed
          updateUploadSpeed();
        }
      }
    );

    // No need to reset individual chunk speeds as we're tracking globally

    // Those parts are formatted for S3 with PascalCase keys
    uploadedParts.push({
      PartNumber: parts[i].part_number,
      ETag: response.headers.etag
    });
  }

  // Complete multipart upload
  const data = await completeUpload({
    file_key,
    upload_id,
    parts: uploadedParts
  });

  return data.location;
};

const shortenFileName = (fileName, maxLength = 120) => {
  if (fileName.length > maxLength) {
    return `${fileName.slice(0, maxLength - 16)}___${fileName.slice(-12)}`;
  }
  return fileName;
};

// Check if file type matches the accept attribute
const isValidFileType = (file) => {
  if (props.accept === '*/*') return true;
  
  const acceptedTypes = props.accept.split(',').map(type => type.trim());
  
  if (acceptedTypes.some(type => {
    if (type.endsWith('/*')) {
      const category = type.split('/')[0];
      return file.type.startsWith(`${category}/`);
    }
    if (type.startsWith('.')) {
      const extension = type.toLowerCase();
      return file.name.toLowerCase().endsWith(extension);
    }
    return type === file.type;
  })) {
    return true;
  }
  
  return false;
};

const processFile = async (file) => {
  if (!isValidFileType(file)) {
    error.value = 'File type not allowed.';
    emit('error', error.value);
    return;
  }
  
  if (file.size > props.maxFileSize) {
    error.value = `File size exceeds maximum limit of ${formatFileSize(props.maxFileSize)}`;
    emit('error', error.value);
    return;
  }

  const fileExtension = file.name.split('.').pop().toLowerCase();
  if (fileExtension.length > 4) {
    error.value = 'File extension is invalid';
    emit('error', error.value);
    return;
  }

  // Get image or video metadata
  const imageMetadata = await getImageMetadata(file);
  const videoMetadata = await getVideoMetadata(file);

  // Validate image dimensions
  if (imageMetadata.width && props.reqFileWidth && imageMetadata.height && props.reqFileHeight) {
    if (imageMetadata.width != props.reqFileWidth || imageMetadata.height != props.reqFileHeight) {
      error.value = `Image dimensions must be ${props.reqFileWidth}x${props.reqFileHeight} pixels. Uploaded image is ${imageMetadata.width}x${imageMetadata.height} pixels.`;
      emit('error', error.value);
      return;
    }
  }

  const fileNameWithoutExtension = shortenFileName(file.name.split('.').slice(0, -1).join('.'), 120);

  currentFile.value = file;
  isUploading.value = true;
  uploadProgress.value = 0;
  error.value = '';

  try {
    // Get pre-signed URL(s)
    const data = await getUploadUrl({
      file_extension: fileExtension,
      file_type: file.type,
      file_size: file.size,
      file_name: fileNameWithoutExtension,
      folder: props.folder || ''
    });

    var url = '';
    if (data.parts) {
      // Multipart upload
      url = await handleMultipartUpload(file, data);
    } else {
      // Single-part upload
      url = await handleSingleUpload(file, data);
    }

    emit('success', {
      file_key: data.file_key,
      file_name: `${fileNameWithoutExtension}.${fileExtension}`,
      file_size: file.size,
      file_type: file.type,
      url: url,
      image_metadata: imageMetadata,
      video_metadata: videoMetadata
    });
  } catch (err) {
    error.value = err.response?.data?.error || err.message || 'An error occurred during upload';
    emit('error', error.value);
  } finally {
    isUploading.value = false;
    chunkProgress.value = [];
    lastUpdateTime.value = 0;
    lastTotalUploaded.value = 0;
    uploadSpeed.value = 0;
    currentFile.value = null;
  }
};

onMounted(() => {
  try {
    initMediaInfo();
  } catch (e) {
    console.warn('Failed to initialize MediaInfo:', e);
  }
});
</script>

<style scoped>
@import '@/styles/file-upload.css';
</style>
